import {
    AuthenticatePinCodeResponse,
    AuthenticatePinCodeResult
} from '@dtos/pin-code';
import type { AuthenticationQueryParams } from '@dtos/query-params';
import { IPinCodeService } from '@services/pin-code-service';
import { getMillisecondDiffFromNow } from '@services/time-query';
import { IAuthorisationStore } from '@stores/authorisation/authorisation-store';
import {
    action,
    computed,
    observable
} from 'mobx';

/**
 * Store responsible for authentication via PIN Code.
 * Expires authentication via timeout or manual trigger.
 */
export interface IAuthenticationStore {
    readonly authenticationResult: AuthenticatePinCodeResult | null;
    readonly didFailAuthentication: boolean;
    readonly hasActiveSession: boolean;

    authenticatePinCode(params: AuthenticationQueryParams, pinCode: string): Promise<AuthenticatePinCodeResult>;
    expireAuthentication(): void;
    resetAuthenticationAttempt(): void;
}

type TimerHandle = number;

export class AuthenticationStore implements IAuthenticationStore {
    @observable
    authenticationResult: AuthenticatePinCodeResult | null = null;

    // unsure what the timer type is in the browser
    @observable
    private tokenExpiryTimer: TimerHandle | null = null;

    constructor(
        private authorisationStore: IAuthorisationStore,
        // @ts-ignore
        private pinCodeService: IPinCodeService
    ) {
    }

    @computed
    get didFailAuthentication(): boolean {
        return (
            this.authenticationResult === AuthenticatePinCodeResult.Failed ||
            // temporary until we work out another message to tell the difference
            this.authenticationResult === AuthenticatePinCodeResult.RequestFailed
        );
    }

    @computed
    get hasActiveSession(): boolean {
        const didAuthenticateSuccessfully = this.authenticationResult === AuthenticatePinCodeResult.Success;
        return didAuthenticateSuccessfully && this.tokenExpiryTimer !== null;
    }

    @action.bound
    async authenticatePinCode(
        params: AuthenticationQueryParams,
        pinCode: string
    ): Promise<AuthenticatePinCodeResult> {
        this.authenticationResult = await this.doPinCodeAuthentication(pinCode, params);

        return this.authenticationResult;
    }

    @action.bound
    private async doPinCodeAuthentication(pinCode: string, params: AuthenticationQueryParams) {
        if (!params?.authorizedRepresentativeId) {
            return AuthenticatePinCodeResult.Failed;
        }

        let result: AuthenticatePinCodeResponse | null;
        try {
            result = await this.pinCodeService.authenticateWithPinCode({
                authorizedRepresentativeId: params.authorizedRepresentativeId,
                pinCode
            });
        } catch (error) {
            // Could be errors when requesting the guest token for the endpoint
            // Possibly show a different message?
            return AuthenticatePinCodeResult.RequestFailed;
        }

        if (!result) {
            return AuthenticatePinCodeResult.Failed;
        }

        const {
            expiryDate,
            token
        } = result;

        this.authorisationStore.acceptToken(token);
        this.startExpiryTimer(expiryDate);
        // TODO: would also have to check endpoint calls for expiry as well somehow

        return AuthenticatePinCodeResult.Success;
    }

    @action.bound
    resetAuthenticationAttempt() {
        this.authenticationResult = null;
    }

    expireAuthentication = () => {
        this.authorisationStore.expireToken();
        this.cancelExpiryTimer();
    }

    startExpiryTimer(endTime: string) {
        const millisecondDiff = getMillisecondDiffFromNow(endTime);
        const endTimeAsRelativeMilliseconds = Math.max(millisecondDiff, 0);
        this.tokenExpiryTimer = window.setTimeout(this.expireAuthentication, endTimeAsRelativeMilliseconds);
    }

    cancelExpiryTimer() {
        if (this.tokenExpiryTimer !== null) {
            window.clearTimeout(this.tokenExpiryTimer);
            this.tokenExpiryTimer = null;
        }
    }
}



// WEBPACK FOOTER //
// ./src/stores/authentication/authentication-store.ts